import React, { useEffect, useState, useMemo, useRef } from "react";
import { Box, Button, Typography, Modal, Alert, Grid } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ApiSelect from "../form/ApiSelect";
import AvatarPicker from "../form/AvatarPicker";
import FormTextField from "../form/FormTextField";
import FormMultilineField from "../form/FormMultilineField";
import { KATAKANA_ONLY } from "../../../constants/validationUtils";

const modalStyle = {
  display: "flex",
  flexDirection: "column",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "758px",
  maxHeight: "80vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  pl: "78px",
  pb: "75px",
  gap: "25px",
};

const UserModal = ({
  open,
  onClose,
  mode,
  initialValues,
  onSubmit,
  currentCompanyUser,
  allUsers,
  companyRoleKey,
}) => {
  const [number, setNumber] = useState("");
  const [lastName, setLastName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastNameKana, setLastNameKana] = useState("");
  const [firstNameKana, setFirstNameKana] = useState("");
  const [email, setEmail] = useState("");
  const [prText, setPrText] = useState("");
  const [department_id, setDepartmentId] = useState(
    initialValues?.department_id ?? null,
  );
  const [managerId, setManagerId] = useState(initialValues?.manager_id ?? null);
  const [errors, setErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [validationTriggered, setValidationTriggered] = useState(false);
  const [avatar, setAvatar] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [removeAvatar, setRemoveAvatar] = useState(false);
  const contentRef = useRef(null);
  const alertRef = useRef(null);

  const isEmployee = initialValues?.role === "employee";
  const isGeneral = initialValues?.role === "general";
  const isAdmin = initialValues?.role === "admin";

  const generalIndex = useMemo(() => {
    if (!Array.isArray(allUsers)) return "";
    const sorted = [...allUsers].sort((a, b) => a.id - b.id);
    const generals = sorted.filter((user) => user.role === "general");

    if (mode === "new" && initialValues?.generalIndex)
      return initialValues.generalIndex;
    if (initialValues?.generalIndex) return initialValues.generalIndex;

    const targetId = initialValues?.id;
    if (!targetId) return "";
    const index = generals.findIndex((user) => user.id === targetId);
    return index >= 0 ? index + 1 : "";
  }, [initialValues?.id, initialValues?.generalIndex, allUsers, mode]);

  const isOwnCard = currentCompanyUser?.id === initialValues?.id;
  const isTargetAdmin = isAdmin;
  const isCurrentGeneral = currentCompanyUser?.role === "general";

  const isNameDisabled = isEmployee
    ? false
    : (isCurrentGeneral && isOwnCard) || // generalは自分の氏名編集不可
      (!isOwnCard && isCurrentGeneral) || // generalは他人編集不可
      (currentCompanyUser?.role === "admin" && isTargetAdmin); // adminでもadmin相手は不可

  const isEmailDisabled = isEmployee
    ? false
    : (isCurrentGeneral && isOwnCard) || // generalは自分のメールアドレス編集不可
      (!isOwnCard && isCurrentGeneral) || // generalは他人編集不可
      (currentCompanyUser?.role === "admin" && isTargetAdmin); // adminでもadmin相手は不可

  // new は全員編集可、edit のときは admin だけ編集可（社員番号、担当者）
  const isEditableOnlyByAdminOnEdit =
    mode === "edit" && currentCompanyUser?.role !== "admin";

  const isPrTextDisabled = isEmployee ? false : !isOwnCard && isCurrentGeneral; // generalは他人編集不可

  // 値変更 + エラークリアをまとめたハンドラー
  const handleChange = (setter, fieldName) => (e) => {
    setter(e.target.value);

    if (fieldErrors[fieldName]) {
      setFieldErrors((prev) => ({
        ...prev,
        [fieldName]: undefined,
      }));
    }
  };

  const handleSubmit = async () => {
    const rules = [
      // 必須チェック
      {
        field: "lastName",
        condition: lastName.trim() === "",
        message: "姓は入力必須です。",
      },
      {
        field: "firstName",
        condition: firstName.trim() === "",
        message: "名は入力必須です。",
      },
      {
        field: "lastNameKana",
        condition: lastNameKana.trim() === "",
        message: "セイは入力必須です。",
      },
      {
        field: "firstNameKana",
        condition: firstNameKana.trim() === "",
        message: "メイは入力必須です。",
      },
      {
        field: "email",
        condition: !isEmployee && email.trim() === "",
        message: "メールアドレスは入力必須です。",
      },

      // 全角カタカナチェック
      {
        field: "lastNameKana",
        condition:
          lastNameKana.trim() !== "" && !KATAKANA_ONLY.test(lastNameKana),
        message: "セイは全角カタカナで入力してください。",
      },
      {
        field: "firstNameKana",
        condition:
          firstNameKana.trim() !== "" && !KATAKANA_ONLY.test(firstNameKana),
        message: "メイは全角カタカナで入力してください。",
      },
    ];

    const newErrors = [];
    const fe = {};

    rules.forEach(({ field, condition, message }) => {
      if (condition) {
        newErrors.push(message);
        fe[field] = message; // フィールド別エラー（赤枠用）もセット
      }
    });

    setValidationTriggered(true);
    setErrors(newErrors);
    setFieldErrors(fe);
    if (newErrors.length > 0) return;

    // 以降は送信（ファイルも同梱するため FormData を使用）
    const fd = new FormData();

    // 編集時ID
    if (mode === "edit")
      fd.append("company_user[id]", String(initialValues?.id));

    // 社員番号
    if (!isEditableOnlyByAdminOnEdit) {
      fd.append("company_user[number]", number);
    }

    // 氏名
    if (!isNameDisabled) {
      fd.append("company_user[last_name]", lastName);
      fd.append("company_user[first_name]", firstName);
      fd.append("company_user[last_name_kana]", lastNameKana);
      fd.append("company_user[first_name_kana]", firstNameKana);
    }
    // メール
    if (!isEmailDisabled) {
      fd.append("company_user[email]", email);
    }

    // 部署（department_id があれば常に送る）
    if (department_id != null) {
      fd.append(
        "company_user[department_membership_attributes][department_id]",
        String(department_id),
      );
    }
    // 担当者（未選択なら送らない）
    if (managerId != null && managerId !== "") {
      fd.append("company_user[manager_id]", String(managerId));
    }
    // PR文（employee は PR文送らない）
    if (!isEmployee) {
      fd.append("company_user[pr_text]", prText);
    }
    // 役割
    fd.append("company_user[role]", initialValues?.role);

    // employee かつ 画像が選択されている場合は送る
    if (isEmployee && avatar) {
      fd.append("company_user[avatar]", avatar);
    }

    // ×で削除された場合
    if (isEmployee && removeAvatar) {
      fd.append("company_user[remove_avatar]", "1");
    }

    try {
      await onSubmit(fd, { mode, id: initialValues?.id });
    } catch (errs) {
      const server = errs?.errors || errs;
      const nextFieldErrors = {};
      const flatMessages = [];

      if (server && typeof server === "object" && !Array.isArray(server)) {
        Object.entries(server).forEach(([attr, msgs]) => {
          const arr = Array.isArray(msgs) ? msgs : [msgs].filter(Boolean);
          flatMessages.push(...arr);
          const isEmailAttr =
            attr === "email" || attr.includes("メールアドレス");
          const isNumberAttr = attr === "number" || attr.includes("社員番号");
          if (isEmailAttr) nextFieldErrors.email = arr[0];
          if (isNumberAttr) nextFieldErrors.number = arr[0];
        });
      } else if (Array.isArray(server)) {
        // ["メールアドレスはすでに存在します。", ...]
        server.forEach((m) => {
          const s = String(m);
          flatMessages.push(s);
          if (s.includes("メールアドレス")) nextFieldErrors.email = s;
          if (s.includes("社員番号")) nextFieldErrors.number = s;
        });
      } else if (typeof server === "string") {
        flatMessages.push(server);
        if (server.includes("メールアドレス")) nextFieldErrors.email = server;
        if (server.includes("社員番号")) nextFieldErrors.number = server;
      } else {
        flatMessages.push("バリデーションエラーが発生しました。");
      }

      setFieldErrors(nextFieldErrors);
      setErrors(flatMessages);
      setValidationTriggered(true);

      // 上へスクロール＆アラートにフォーカス
      setTimeout(() => {
        contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        alertRef.current?.focus?.();
      }, 0);
    }
  };

  useEffect(() => {
    if (open && mode === "edit" && initialValues) {
      setNumber(initialValues.number || "");
      setLastName(initialValues.last_name || "");
      setFirstName(initialValues.first_name || "");
      setLastNameKana(initialValues.last_name_kana || "");
      setFirstNameKana(initialValues.first_name_kana || "");
      setEmail(initialValues.email || "");
      setPrText(initialValues.prText ?? "");
      setDepartmentId(
        initialValues.department_id ?? initialValues.departmentId ?? null,
      );
      setManagerId(initialValues.manager_id ?? null);
      setAvatar(null);
      setAvatarUrl(initialValues.avatar_url ?? initialValues.avatarUrl ?? null);
    } else {
      setNumber("");
      setLastName("");
      setFirstName("");
      setLastNameKana("");
      setFirstNameKana("");
      setEmail("");
      setPrText("");
      setDepartmentId(null);
      setManagerId(null);
      setAvatar(null);
      setAvatarUrl(null);
    }
    if (!open) {
      setErrors([]);
      setFieldErrors({});
      setValidationTriggered(false);
    }
  }, [initialValues, mode, open]);

  // エラーが出たらモーダル内スクロールを先頭へ
  useEffect(() => {
    if (validationTriggered && errors.length > 0) {
      contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [validationTriggered, errors.length]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box ref={contentRef} sx={modalStyle}>
        <Box sx={{ textAlign: "right", mt: "8px" }}>
          <Button onClick={onClose}>
            <CloseIcon sx={{ color: "#767676" }} />
          </Button>
        </Box>

        {validationTriggered && errors.length > 0 && (
          <Alert
            ref={alertRef}
            tabIndex={-1}
            severity="error"
            sx={{
              width: "612px",
              bgcolor: "#FFF5F5",
              color: "#C53030",
              borderRadius: 2,
            }}
          >
            {errors.map((err, i) => (
              <div key={i}>{err}</div>
            ))}
          </Alert>
        )}

        {/* 見出し：役割に応じて表示 */}
        <Typography sx={{ fontSize: "24px", color: "#979797" }}>
          {isAdmin
            ? "代表者"
            : isGeneral && generalIndex
              ? `担当者${generalIndex}`
              : isEmployee
                ? "メンバー"
                : ""}
        </Typography>

        {/* 写真：employee のみ表示 */}
        {isEmployee && (
          <Box sx={{ width: { xs: "100%", md: 612 }, mb: "20px" }}>
            <AvatarPicker
              value={{ file: avatar, url: avatarUrl }}
              onChange={({ file, url }) => {
                setAvatar(file);
                setAvatarUrl(url);
                setRemoveAvatar(!file && !url);
              }}
              onError={(msg) => {
                setValidationTriggered(true);
                setErrors((prev) =>
                  prev.includes(msg) ? prev : [...prev, msg],
                );
              }}
            />
          </Box>
        )}

        {/* 社員番号 */}
        <Box sx={{ width: { xs: "100%", md: 612 } }}>
          <FormTextField
            label="社員番号"
            name="number"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            error={Boolean(fieldErrors.number)}
            disabled={isEditableOnlyByAdminOnEdit}
          />
        </Box>

        {/* 氏名 */}
        <Box sx={{ width: { xs: "100%", md: 612 } }}>
          <Grid container spacing={2}>
            {/* 姓 */}
            <Grid item xs={12} md={6}>
              <FormTextField
                label="姓"
                name="lastName"
                value={lastName}
                onChange={handleChange(setLastName, "lastName")}
                error={Boolean(fieldErrors.lastName)}
                isRequired
                disabled={isNameDisabled}
              />
            </Grid>

            {/* 名 */}
            <Grid item xs={12} md={6}>
              <FormTextField
                label="名"
                name="firstName"
                value={firstName}
                onChange={handleChange(setFirstName, "firstName")}
                error={Boolean(fieldErrors.firstName)}
                isRequired
                disabled={isNameDisabled}
              />
            </Grid>
          </Grid>
        </Box>

        {/* 氏名(カナ) */}
        <Box sx={{ width: { xs: "100%", md: 612 } }}>
          <Grid container spacing={2}>
            {/* セイ */}
            <Grid item xs={12} md={6}>
              <FormTextField
                label="セイ"
                name="lastNameKana"
                value={lastNameKana}
                onChange={handleChange(setLastNameKana, "lastNameKana")}
                error={Boolean(fieldErrors.lastNameKana)}
                isRequired
                disabled={isNameDisabled}
              />
            </Grid>

            {/* メイ */}
            <Grid item xs={12} md={6}>
              <FormTextField
                label="メイ"
                name="firstNameKana"
                value={firstNameKana}
                onChange={handleChange(setFirstNameKana, "firstNameKana")}
                error={Boolean(fieldErrors.firstNameKana)}
                isRequired
                disabled={isNameDisabled}
              />
            </Grid>
          </Grid>
        </Box>

        {/* メールアドレス（共通で必須） */}
        <Box sx={{ width: { xs: "100%", md: 612 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormTextField
                label="メールアドレス"
                name="email"
                value={email}
                onChange={handleChange(setEmail, "email")}
                error={Boolean(fieldErrors.email)}
                isRequired={!isEmployee}
                disabled={isEmailDisabled}
              />
            </Grid>
          </Grid>
        </Box>

        {/* 担当者：employeeのみ表示 */}
        {isEmployee && (
          <Box>
            <ApiSelect
              value={managerId ?? ""}
              onChange={(val) => setManagerId(val === "" ? null : Number(val))}
              endpoint="/company/users.json"
              label="担当者"
              isRequired={false}
              error={false}
              blankLabel="未設定"
              disabled={isEditableOnlyByAdminOnEdit}
            />
          </Box>
        )}

        {/* 部署：利用目的がマネジメントの企業のみ表示 */}
        {companyRoleKey === "management" && (
          <Box>
            <ApiSelect
              value={department_id ?? ""}
              onChange={(val) =>
                setDepartmentId(val === "" || val == null ? null : val)
              }
              endpoint="/company/settings.json"
              label="部署"
              isRequired={false}
              error={false}
              blankLabel="未設定"
            />
          </Box>
        )}

        {/* PR文：employee では非表示 */}
        {!isEmployee && (
          <Box sx={{ width: { xs: "100%", md: 612 } }}>
            <FormMultilineField
              label="PR文（最大70字まで）"
              name="prText"
              value={prText}
              onChange={(e) => setPrText(e.target.value)}
              rows={2}
              maxLength={70}
              error={Boolean(fieldErrors.prText)}
              placeholder=""
              isRequired={false}
              disabled={isPrTextDisabled}
            />
          </Box>
        )}

        <Box>
          <Button
            onClick={onClose}
            sx={{
              px: "25px",
              py: "10px",
              border: "1px solid #454545",
              borderRadius: 0,
              color: "#000",
              mr: "23px",
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleSubmit}
            sx={{
              px: "20px",
              py: "11px",
              bgcolor: "#292929",
              color: "#FFF",
              borderRadius: 0,
              "&:hover": { bgcolor: "#666666" },
            }}
          >
            {mode === "edit" ? "更新" : "追加"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default UserModal;
